import { useState, useRef, useEffect } from 'react';
import './CreateCommunityEventPanel.css';
import { communityEventsAPI, eventsAPI } from './services/api';
import { useTranslation } from 'react-i18next';
import { getHourCycle } from './utils/dateUtils';
import InviteFriendsDialog from './InviteFriendsDialog';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { enUS, he, bg } from 'date-fns/locale';
import ReCAPTCHA from 'react-google-recaptcha';

const DATE_FNS_LOCALE = { en: enUS, he, bg };

function CreateCommunityEventPanel({ currentUser, onEventCreated }) {
  const { t, i18n } = useTranslation();
  const [eventType, setEventType] = useState('public'); // 'public' or 'private'
  const [eventName, setEventName] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [eventPhoto, setEventPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [suitableForPeople, setSuitableForPeople] = useState('');
  const [isOrganizer, setIsOrganizer] = useState(true); // Auto-select for all
  const [eventDateTime, setEventDateTime] = useState(null);
  const [creating, setCreating] = useState(false);
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [showFriendDialog, setShowFriendDialog] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  // Helper function to update location
  const updateLocation = (lat, lng, formattedAddress) => {
    setAddress(formattedAddress);
    setLatitude(lat);
    setLongitude(lng);

    // Update input field
    if (addressInputRef.current) {
      addressInputRef.current.value = formattedAddress;
    }

    // Update marker on map
    if (markerRef.current && mapInstanceRef.current) {
      markerRef.current.setPosition({ lat, lng });
      mapInstanceRef.current.panTo({ lat, lng });
    }
  };

  const handleFriendToggle = (userId) => {
    setSelectedFriends(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Initialize Google Places Autocomplete
  useEffect(() => {
    const initAutocomplete = () => {
      if (!addressInputRef.current) {
        return;
      }

      if (!window.google || !window.google.maps || !window.google.maps.places) {
        setTimeout(initAutocomplete, 100);
        return;
      }

      try {
        if (autocompleteRef.current) {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        }

        const autocomplete = new window.google.maps.places.Autocomplete(
          addressInputRef.current,
          { types: ['geocode', 'establishment'] }
        );

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (place.geometry) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            updateLocation(lat, lng, place.formatted_address);
          }
        });

        autocompleteRef.current = autocomplete;
      } catch (error) {
        console.error('Error initializing autocomplete:', error);
      }
    };

    initAutocomplete();

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, []);

  // Initialize Google Map
  useEffect(() => {
    const initMap = () => {
      if (!mapRef.current) {
        return;
      }

      if (!window.google || !window.google.maps) {
        setTimeout(initMap, 100);
        return;
      }

      try {
        // Default to user's location or a central location
        const defaultLat = 42.6977;
        const defaultLng = 23.3219; // Sofia, Bulgaria

        const map = new window.google.maps.Map(mapRef.current, {
          center: { lat: defaultLat, lng: defaultLng },
          zoom: 13
        });

        const marker = new window.google.maps.Marker({
          map: map,
          position: { lat: defaultLat, lng: defaultLng },
          draggable: true
        });

        // When marker is dragged, update address
        marker.addListener('dragend', () => {
          const position = marker.getPosition();
          const lat = position.lat();
          const lng = position.lng();

          // Reverse geocode to get address
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results[0]) {
              updateLocation(lat, lng, results[0].formatted_address);
            }
          });
        });

        // When map is clicked, move marker
        map.addListener('click', (event) => {
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();

          marker.setPosition({ lat, lng });

          // Reverse geocode to get address
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results[0]) {
              updateLocation(lat, lng, results[0].formatted_address);
            }
          });
        });

        mapInstanceRef.current = map;
        markerRef.current = marker;
      } catch (error) {
        console.error('Error initializing map:', error);
      }
    };

    initMap();
  }, []);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEventPhoto(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateEvent = async () => {
    if (!captchaToken) return;

    if (!eventName.trim()) {
      alert(t('createCommunityEvent.alerts.enterName'));
      return;
    }

    if (!address || !latitude || !longitude) {
      alert(t('createCommunityEvent.alerts.selectLocation'));
      return;
    }

    if (!suitableForPeople.trim() || isNaN(suitableForPeople) || parseInt(suitableForPeople) < 1) {
      alert(t('createCommunityEvent.alerts.enterPeople'));
      return;
    }

    // For private events or when user is organizer, date/time is required
    if ((eventType === 'private' || isOrganizer) && !eventDateTime) {
      alert(t('createCommunityEvent.alerts.selectDateTime'));
      return;
    }

    // Validate that events are at least 30 minutes in the future
    if ((eventType === 'private' || isOrganizer) && eventDateTime) {
      const eventDateTimeObj = eventDateTime;
      const now = new Date();
      const minFutureTime = new Date(now.getTime() + 30 * 60 * 1000); // 30 minutes from now

      if (eventDateTimeObj < minFutureTime) {
        const timeDiff = Math.round((minFutureTime - eventDateTimeObj) / (60 * 1000));
        if (eventDateTimeObj < now) {
          alert(t('createCommunityEvent.alerts.pastDate'));
        } else {
          alert(t('createCommunityEvent.alerts.tooSoon', { minutes: timeDiff }));
        }
        return;
      }
    }

    try {
      setCreating(true);

      // Extract hashtags from name and description
      const hashtagRegex = /#(\w+)/g;
      const extractedHashtags = [
        ...(eventName.match(hashtagRegex) || []),
        ...(description.match(hashtagRegex) || [])
      ].map(tag => tag.slice(1)); // Remove the leading #
      const uniqueHashtags = [...new Set(extractedHashtags)];

      const pad = (n) => String(n).padStart(2, '0');
      const toDateStr = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
      const toTimeStr = (d) => `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;

      // Private events save to Events table
      if (eventType === 'private') {
        const eventData = {
          name: eventName,
          description: description,
          address: address,
          latitude: latitude,
          longitude: longitude,
          date: toDateStr(eventDateTime),
          time: toTimeStr(eventDateTime),
          eventType: 1,
          hashtags: uniqueHashtags,
          friendIds: selectedFriends,
          photo: eventPhoto,
          suitableForPeople: parseInt(suitableForPeople)
          // Note: communityEventId is NOT included, so it will be null (private event)
        };

        await eventsAPI.createWithPhoto(eventData);
        alert(t('createCommunityEvent.alerts.createdPrivate'));
      } else {
        // Public events save to CommunityEvents table
        const eventData = {
          name: eventName,
          description: description,
          address: address,
          latitude: latitude,
          longitude: longitude,
          eventType: 1,
          hashtags: uniqueHashtags,
          photo: eventPhoto,
          suitableForPeople: parseInt(suitableForPeople)
        };

        if (isOrganizer && eventDateTime) {
          eventData.date = toDateStr(eventDateTime);
          eventData.time = toTimeStr(eventDateTime);
        }

        await communityEventsAPI.create(eventData);
        alert(t('createCommunityEvent.alerts.createdPublic'));
      }

      onEventCreated();
    } catch (error) {
      console.error('Error creating event:', error);
      captchaRef.current?.reset();
      setCaptchaToken(null);
      const errorMessage = error.response?.data || error.message || 'Unknown error';
      alert(t('createCommunityEvent.alerts.failed', { error: JSON.stringify(errorMessage) }));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="create-community-event-panel">
      <h3>{t('createCommunityEvent.title')}</h3>
      <p className="community-event-description">
        {t('createCommunityEvent.description')}
      </p>
      <div className="duration-warning">
        {t('createCommunityEvent.durationWarning')}
      </div>

      <div className="event-form">
        {/* Event Type Selection */}
        <div className="form-group event-type-section">
          <label>{t('createCommunityEvent.eventTypeLabel')}</label>
          <div className="radio-group">
            <label className={`radio-option${eventType === 'public' ? ' selected' : ''}`}>
              <input
                type="radio"
                name="eventType"
                checked={eventType === 'public'}
                onChange={() => setEventType('public')}
              />
              <span className="radio-text">{t('createCommunityEvent.publicEvent')}</span>
            </label>
            <label className={`radio-option${eventType === 'private' ? ' selected' : ''}`}>
              <input
                type="radio"
                name="eventType"
                checked={eventType === 'private'}
                onChange={() => setEventType('private')}
              />
              <span className="radio-text">{t('createCommunityEvent.privateEvent')}</span>
            </label>
          </div>
          <p className="helper-text">
            {eventType === 'public'
              ? t('createCommunityEvent.publicHelper')
              : t('createCommunityEvent.privateHelper')}
          </p>
        </div>
        <div className="form-group">
          <label>{t('createCommunityEvent.eventNameLabel')}</label>
          <input
            type="text"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            placeholder={t('createCommunityEvent.eventNamePlaceholder')}
          />
        </div>

        <div className="form-group">
          <label>{t('createCommunityEvent.descriptionLabel')}</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('createCommunityEvent.descriptionPlaceholder')}
            rows="4"
          />
          <div className="predefined-hashtags">
            {['#CommunityWork', '#Education', '#Art', '#Sport', '#Tourism'].map(tag => (
              <button
                key={tag}
                type="button"
                className="hashtag-chip"
                onClick={() => setDescription(prev => prev ? `${prev} ${tag}` : tag)}
              >
                {tag}
              </button>
            ))}
          </div>
          <p className="helper-text">{t('createCommunityEvent.descriptionHelper')}</p>
        </div>

        <div className="form-group">
          <label>{t('createCommunityEvent.photoLabel')}</label>
          <input
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className="photo-input"
          />
          {photoPreview && (
            <div className="photo-preview">
              <img src={photoPreview} alt="Event preview" />
            </div>
          )}
          <p className="helper-text">{t('createCommunityEvent.photoHelper')}</p>
        </div>

        <div className="form-group">
          <label>{t('createCommunityEvent.suitableForLabel')}</label>
          <input
            type="number"
            min="1"
            value={suitableForPeople}
            onChange={(e) => setSuitableForPeople(e.target.value)}
            placeholder={t('createCommunityEvent.suitableForPlaceholder')}
          />
          <p className="helper-text">{t('createCommunityEvent.suitableForHelper')}</p>
        </div>

        <div className="form-group">
          <label>{t('createCommunityEvent.locationLabel')}</label>
          <input
            ref={addressInputRef}
            type="text"
            defaultValue={address}
            placeholder={t('createCommunityEvent.locationPlaceholder')}
            onBlur={(e) => setAddress(e.target.value)}
          />
          <p className="helper-text">{t('createCommunityEvent.locationHelper')}</p>

          {/* Google Map */}
          <div ref={mapRef} className="location-map"></div>
        </div>

        {/* Role Selection - Only for public events */}
        {eventType === 'public' && (
          <div className="form-group datetime-section">
            <div className="radio-group">
              <label className={`radio-option${isOrganizer ? ' selected' : ''}`}>
                <input
                  type="radio"
                  name="eventRole"
                  checked={isOrganizer}
                  onChange={() => setIsOrganizer(true)}
                  disabled={eventType === 'private'}
                />
                <span className="radio-text">{t('createCommunityEvent.organizerRole')}</span>
              </label>
              <label className={`radio-option${!isOrganizer ? ' selected' : ''}`}>
                <input
                  type="radio"
                  name="eventRole"
                  checked={!isOrganizer}
                  onChange={() => setIsOrganizer(false)}
                  disabled={eventType === 'private'}
                />
                <span className="radio-text">{t('createCommunityEvent.proposalRole')}</span>
              </label>
            </div>
          </div>
        )}

        {/* Date and Time - Required for private events or when user is organizer */}
        {(eventType === 'private' || isOrganizer) && (
          <div className="form-group">
            <label>{t('createCommunityEvent.dateTimeLabel')} {eventType === 'private' ? t('createCommunityEvent.required') : ''}</label>
            <DatePicker
              selected={eventDateTime}
              onChange={(date) => setEventDateTime(date)}
              showTimeSelect
              timeFormat={getHourCycle(i18n.language) === 'h23' ? 'HH:mm' : 'h:mm aa'}
              timeIntervals={15}
              dateFormat={getHourCycle(i18n.language) === 'h23' ? 'EEEE, d MMMM yyyy HH:mm' : 'EEEE, MMMM d, yyyy h:mm aa'}
              locale={DATE_FNS_LOCALE[i18n.language] ?? enUS}
              placeholderText={t('createCommunityEvent.dateTimePlaceholder')}
              minDate={new Date()}
              className="datepicker-input"
              popperPlacement="bottom-start"
            />
            <p className="helper-text">{t('createCommunityEvent.dateTimeHelper')}</p>
          </div>
        )}

        {/* Invite Friends - Only for private events */}
        {eventType === 'private' && (
          <div className="form-group">
            <label>{t('createCommunityEvent.inviteFriendsLabel')}</label>
            <button
              className="select-friends-btn"
              type="button"
              onClick={() => setShowFriendDialog(true)}
            >
              {selectedFriends.length > 0
                ? t('createCommunityEvent.friendsSelected', { count: selectedFriends.length })
                : t('createCommunityEvent.selectFriends')}
            </button>
            <p className="helper-text">{t('createCommunityEvent.inviteFriendsHelper')}</p>
          </div>
        )}

        <ReCAPTCHA
          ref={captchaRef}
          sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
          onChange={(token) => setCaptchaToken(token)}
          onExpired={() => setCaptchaToken(null)}
        />

        <button
          className="create-btn"
          onClick={handleCreateEvent}
          disabled={creating || !captchaToken}
        >
          {creating
            ? t('createCommunityEvent.creating')
            : eventType === 'private'
              ? t('createCommunityEvent.createPrivate')
              : t('createCommunityEvent.createPublic')}
        </button>
      </div>

      <InviteFriendsDialog
        isOpen={showFriendDialog}
        onClose={() => setShowFriendDialog(false)}
        selectedFriends={selectedFriends}
        onToggle={handleFriendToggle}
        currentUser={currentUser}
      />
    </div>
  );
}

export default CreateCommunityEventPanel;
